// IndexedDB read cache for the api() wrapper.
//
// Strategy: every successful GET to /api/* gets persisted by URL +
// current user id. When the network is down (fetch throws, or the
// response is a 5xx), api() returns the most recent cached copy and
// emits a `fk:served-from-cache` event so the UI can show a "showing
// cached data" badge.
//
// Writes (POST/PATCH/DELETE) invalidate any cached entries whose path
// could plausibly contain the affected resource — exact-prefix match
// on the path stem, so POST /api/leads invalidates GET /api/leads
// AND GET /api/leads/123. Crude but correct.
//
// Storage budget: ~30MB hard cap with FIFO eviction. iOS WKWebView
// gives ~50MB by default; we leave headroom for the outbox.
//
// Skip list: admin / billing / auth endpoints are never cached so
// sensitive content doesn't sit on the device after sign-out.

const DB_NAME = "fk-cache";
const DB_VERSION = 1;
const STORE = "responses";
const MAX_BYTES = 30 * 1024 * 1024;

const SKIP_PREFIXES = [
  "/api/admin/",
  "/api/billing/",
  "/api/auth/",
  "/api/push/",
  "/api/me/mailboxes",
];

interface CacheRow {
  // Composite key — only one row per user+url survives.
  id: string;
  user_id: string | null;
  url: string;
  // The JSON-encoded response body. Always a string for simplicity.
  body: string;
  // When this entry was written.
  at: number;
  // Approximate byte cost for the cap math (length of `body`).
  size: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("by_user", "user_id");
        store.createIndex("by_at", "at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function key(userId: string | null, url: string): string {
  return `${userId ?? "anon"}::${url}`;
}

function shouldCache(url: string): boolean {
  // We pass the path-or-absolute URL here. Normalize to path for prefix
  // checks so callers can pass either.
  let path = url;
  try {
    if (url.startsWith("http")) path = new URL(url).pathname;
  } catch {
    // Not a valid URL — fall through with whatever we got.
  }
  if (!path.startsWith("/api/")) return false;
  for (const skip of SKIP_PREFIXES) {
    if (path.startsWith(skip)) return false;
  }
  return true;
}

// Cache the user id between calls so callers don't have to thread it
// through. Set by main.tsx after the first /api/me resolves.
let currentUserId: string | null = null;
export function setCacheUser(id: string | null): void {
  // If the user changed, drop everything from the previous user. Avoids
  // session-A-jobs leaking into session-B's offline view.
  if (currentUserId && currentUserId !== id) {
    void clearAllForUser(currentUserId);
  }
  currentUserId = id;
}

export async function cachedGet(url: string): Promise<unknown | null> {
  if (!shouldCache(url)) return null;
  try {
    const db = await openDb();
    return await new Promise<unknown | null>((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key(currentUserId, url));
      req.onsuccess = () => {
        const row = req.result as CacheRow | undefined;
        if (!row) return resolve(null);
        try {
          resolve(JSON.parse(row.body));
        } catch {
          resolve(null);
        }
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function cachePut(url: string, data: unknown): Promise<void> {
  if (!shouldCache(url)) return;
  try {
    const body = JSON.stringify(data);
    const size = body.length;
    if (size > 2_000_000) {
      // Skip huge responses (≥2MB). Indexing them blows the storage
      // budget and the use case for offline-reading a 2MB list is thin.
      return;
    }
    const db = await openDb();
    const row: CacheRow = {
      id: key(currentUserId, url),
      user_id: currentUserId,
      url,
      body,
      at: Date.now(),
      size,
    };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const req = tx.objectStore(STORE).put(row);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    void enforceCapAsync();
  } catch {
    // Quota exceeded / browser blocked — silently no-op. Cache is
    // best-effort; the app must work without it.
  }
}

// Best-effort FIFO eviction when we cross the byte cap. Runs in the
// background after a put.
let evictionInFlight = false;
async function enforceCapAsync(): Promise<void> {
  if (evictionInFlight) return;
  evictionInFlight = true;
  try {
    const db = await openDb();
    const rows: { id: string; size: number; at: number }[] = [];
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const cursor = tx.objectStore(STORE).index("by_at").openCursor();
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c) return resolve();
        const v = c.value as CacheRow;
        rows.push({ id: v.id, size: v.size, at: v.at });
        c.continue();
      };
      cursor.onerror = () => resolve();
    });
    let total = rows.reduce((s, r) => s + r.size, 0);
    if (total <= MAX_BYTES) return;
    // Drop oldest entries until under the cap.
    rows.sort((a, b) => a.at - b.at);
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    for (const r of rows) {
      if (total <= MAX_BYTES) break;
      store.delete(r.id);
      total -= r.size;
    }
  } finally {
    evictionInFlight = false;
  }
}

// Invalidate cached GETs that COULD reflect a resource we just mutated.
// e.g. POST /api/leads → drop /api/leads, /api/leads?, /api/leads/123.
export async function invalidateForMutation(mutationUrl: string): Promise<void> {
  let path = mutationUrl;
  try {
    if (mutationUrl.startsWith("http")) path = new URL(mutationUrl).pathname;
  } catch {
    // ignore
  }
  // Walk up to the resource stem — /api/leads/123/notes → /api/leads/123
  // and /api/leads. We invalidate every prefix segment.
  const segments = path.split("/").filter(Boolean);
  const stems = new Set<string>();
  for (let i = segments.length; i >= 2; i--) {
    stems.add("/" + segments.slice(0, i).join("/"));
  }
  if (stems.size === 0) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const cursor = store.openCursor();
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c) return resolve();
        const v = c.value as CacheRow;
        for (const stem of stems) {
          // Match exact OR query-stringed OR sub-path.
          if (v.url === stem || v.url.startsWith(stem + "?") || v.url.startsWith(stem + "/")) {
            store.delete(c.primaryKey);
            break;
          }
        }
        c.continue();
      };
      cursor.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
}

async function clearAllForUser(userId: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      const idx = tx.objectStore(STORE).index("by_user");
      const cursor = idx.openCursor(IDBKeyRange.only(userId));
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c) return resolve();
        c.delete();
        c.continue();
      };
      cursor.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
}
